import { create } from 'zustand'
import { config } from '../config'

type PinchPhase = 'idle' | 'pinching'

type HandState = {
  phase: PinchPhase
  /** 立方体的目标世界坐标（已被映射且 EMA 平滑） */
  targetX: number
  targetY: number
  targetZ: number
  /** Pinch 进入时记录的偏移，让"抓起来不抖回原点" */
  grabOffset: { x: number; y: number; z: number }
  /** 是否在工作 */
  active: boolean
  /** 当前手是否可见 */
  visible: boolean
  lastUpdateMs: number

  ingest: (
    handX: number,
    handY: number,
    handZ: number,
    pinchDistance: number,
    cubeWorldPos: [number, number, number] | null,
  ) => void
  setHandVisible: (v: boolean) => void
  setActive: (a: boolean) => void
  release: () => void
}

const _ema = { x: 0, y: 0, z: 0 }

function clamp(v: number, lo: number, hi: number) {
  return v < lo ? lo : v > hi ? hi : v
}

function mapHandToWorld(handX: number, handY: number, handZ: number) {
  const m = config.hand.mapping
  const nx = m.mirrorX ? 1 - handX : handX
  // 输入是 0-1 屏幕归一化，映射到 xRange/yRange
  const x = m.xRange[0] + nx * (m.xRange[1] - m.xRange[0])
  // Mediapipe y 也是 0-1，0 在顶部
  const y = m.yRange[1] - handY * (m.yRange[1] - m.yRange[0])
  // hand z 是相对深度，数值范围比较奇怪（约 -0.1 ~ 0.1），用 scale 后 clamp
  const zRaw = (m.zCenter - handZ) * m.zScale
  const z = clamp(zRaw, m.zRange[0], m.zRange[1])
  return { x, y, z }
}

export const useHandStore = create<HandState>((set, get) => ({
  phase: 'idle',
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  grabOffset: { x: 0, y: 0, z: 0 },
  active: false,
  visible: false,
  lastUpdateMs: 0,

  ingest: (handX, handY, handZ, pinchDistance, cubeWorldPos) => {
    const aXY = config.hand.emaAlpha
    const aZ = config.hand.emaAlphaZ
    const mapped = mapHandToWorld(handX, handY, handZ)
    _ema.x = aXY * mapped.x + (1 - aXY) * _ema.x
    _ema.y = aXY * mapped.y + (1 - aXY) * _ema.y
    _ema.z = aZ * mapped.z + (1 - aZ) * _ema.z

    const prev = get()
    let phase = prev.phase
    if (phase === 'idle' && pinchDistance < config.hand.pinchEnter) {
      phase = 'pinching'
      // 记录抓取偏移：让立方体保持在原本位置，避免瞬移
      if (cubeWorldPos) {
        set({
          grabOffset: {
            x: cubeWorldPos[0] - _ema.x,
            y: cubeWorldPos[1] - _ema.y,
            z: cubeWorldPos[2] - _ema.z,
          },
        })
      }
    } else if (phase === 'pinching' && pinchDistance > config.hand.pinchExit) {
      phase = 'idle'
    }

    const off = get().grabOffset
    const targetX = clamp(
      _ema.x + (phase === 'pinching' ? off.x : 0),
      config.cube.bounds.x[0],
      config.cube.bounds.x[1],
    )
    const targetY = clamp(
      _ema.y + (phase === 'pinching' ? off.y : 0),
      config.cube.bounds.y[0],
      config.cube.bounds.y[1],
    )
    const targetZ = clamp(
      _ema.z + (phase === 'pinching' ? off.z : 0),
      config.cube.bounds.z[0],
      config.cube.bounds.z[1],
    )

    set({
      phase,
      targetX,
      targetY,
      targetZ,
      visible: true,
      active: true,
      lastUpdateMs: performance.now(),
    })
  },

  setHandVisible: (v) => set({ visible: v }),
  setActive: (a) => set({ active: a }),
  release: () => set({ phase: 'idle' }),
}))
